import React, { memo } from 'react';
import { Phone, Instagram, MessageCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import StarRating from './StarRating';
import FavoriteButton from './FavoriteButton';
import ShareButton from './ShareButton';
import { BusinessLevelBadge } from './BusinessBadges';
import BackendImage from './BackendImage';
import BusinessTagsChips from './BusinessTagsChips';
import { buildBusinessUrl } from '../lib/businessUrl';
import { getCategoryIcon } from '../lib/categoryIcons';
import { useCampanhaForBusiness } from '../hooks/useCampanhaForBusiness';
import { useColetivoForBusiness } from '../hooks/useColetivoForBusiness';
import { Megaphone, Handshake } from 'lucide-react';
import { openWhatsApp } from '../utils/whatsappLink';
import { isBusinessOpenNow, hasActivePromotion } from '../utils/businessHours';

function BusinessCard({ business, showDistance = false, variant = 'vertical' }) {
  const navigate = useNavigate();
  const { municipioSlug } = useParams();
  const isOpen = isBusinessOpenNow(business.horarios);
  const showPromotion = hasActivePromotion(business);
  const horizontal = variant === 'horizontal';
  // `grid` = vertical mas cresce até 100% do container (para listagens em
  // grid onde a coluna define a largura, e não o carrossel).
  const gridFull = variant === 'grid';
  const CategoryIcon = getCategoryIcon(business.categoria);
  const campanha = useCampanhaForBusiness(business.id, municipioSlug);
  const coletivo = useColetivoForBusiness(business.id, municipioSlug);

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    openWhatsApp({
      business,
      municipio: { slug: municipioSlug, nome: business.municipio_nome },
      promocao: business.promocao_ativa || (business.promocao ? { titulo: business.promocao_titulo } : null),
    });
  };

  const handleInstagram = (e) => {
    e.stopPropagation();
    if (business.instagram) {
      const username = business.instagram.replace('@', '');
      window.open(`https://instagram.com/${username}`, '_blank');
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    if (business.telefone) {
      window.location.href = `tel:${business.telefone}`;
    }
  };

  const handleCardClick = () => {
    const seo = buildBusinessUrl(business);
    if (seo.startsWith('/') && !seo.startsWith('/negocio/')) {
      navigate(seo);
      return;
    }
    const basePath = municipioSlug ? `/${municipioSlug}` : '';
    navigate(`${basePath}/negocio/${business.id}`);
  };

  const logoSrc =
    business.imagens?.logo?.medium ||
    business.imagens?.logo?.thumb ||
    business.avatar_url;

  // ─────────────────────────────────────────────────────────────────
  // Horizontal variant — round avatar on the left, content on the right.
  // Used by Explorar grid where vertical hero photos take too much room.
  // ─────────────────────────────────────────────────────────────────
  if (horizontal) {
    return (
      <article
        onClick={handleCardClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
        className="bg-white rounded-xl border border-[var(--border)] shadow-sm hover:shadow-md transition-all overflow-hidden group cursor-pointer relative p-4"
        data-testid={`business-card-${business.id}`}
        role="link"
        tabIndex={0}
        aria-label={`${business.nome}${business.categoria_nome ? `, ${business.categoria_nome}` : ''}${typeof business.rating === 'number' ? `, avaliação ${business.rating} de 5` : ''}`}
      >
        {/* Favorite and Share — overlay top-right */}
        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <ShareButton business={business} size="sm" />
          <FavoriteButton businessId={business.id} size="sm" />
        </div>

        <div className="flex gap-4">
          {/* Logo — quadrado com cantos arredondados (contain preserva a marca inteira). */}
          <div className="flex-shrink-0">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-white ring-1 ring-[var(--border)]">
              {logoSrc ? (
                <BackendImage
                  src={logoSrc}
                  alt={business.nome}
                  className="w-full h-full object-contain object-center block bg-white"
                  fallback={(
                    <div className="w-full h-full flex items-center justify-center text-[var(--primary)]">
                      <CategoryIcon className="w-8 h-8 opacity-70" />
                    </div>
                  )}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--primary)]">
                  <CategoryIcon className="w-8 h-8 opacity-70" />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pr-10">
            <h3
              className="font-bold text-base sm:text-lg leading-tight mb-1 group-hover:text-[var(--primary)] transition-colors line-clamp-2"
              style={{ fontFamily: 'Manrope' }}
            >
              {business.nome}
            </h3>
            <p className="text-xs sm:text-sm text-[var(--text-secondary)] line-clamp-1 mb-2">
              {business.subcategoria
                ? `${business.subcategoria} • ${business.bairro}`
                : `${business.categoria} • ${business.bairro}`}
            </p>

            {business.tags_controladas_ids && business.tags_controladas_ids.length > 0 && (
              <div className="mb-2">
                <BusinessTagsChips business={business} limit={4} testIdPrefix={`biz-card-${business.id}-tag`} />
              </div>
            )}

            <div className="flex flex-wrap gap-1 mb-2">
              <BusinessLevelBadge businessId={business.id} />
              {/* Badge "Verificado" removido da exibição pública (iter 196).
                  O sub-critério continua contando no IRN internamente e
                  aparece apenas em telas administrativas. */}
              {isOpen && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-800">
                  Aberto
                </span>
              )}
              {business.entrega && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-800">
                  Entrega
                </span>
              )}
              {showPromotion && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 text-orange-800">
                  Promoção
                </span>
              )}
              {campanha && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/campanhas/${campanha.campanha_id}`); }}
                  data-testid={`campanha-badge-${business.id}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-white hover:opacity-90"
                  style={{ background: campanha.cor_destaque }}
                  title={`Campanha: ${campanha.campanha_nome}`}
                >
                  <Megaphone className="w-3 h-3 mr-1" />
                  {campanha.campanha_nome.length > 18 ? campanha.campanha_nome.slice(0, 18) + '…' : campanha.campanha_nome}
                </button>
              )}
              {coletivo && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/coletivos/${coletivo.coletivo_slug}`); }}
                  data-testid={`coletivo-badge-${business.id}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 transition"
                  title={`Membro do coletivo: ${coletivo.coletivo_nome}`}
                >
                  <Handshake className="w-3 h-3 mr-1" />
                  {coletivo.coletivo_nome.length > 18 ? coletivo.coletivo_nome.slice(0, 18) + '…' : coletivo.coletivo_nome}
                </button>
              )}
              {(business.fallback_usado === true || (typeof business.text_relevance === 'number' && business.text_relevance < 0.3)) && (
                <span
                  data-testid={`related-match-badge-${business.id}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200"
                  title="Este resultado veio de uma busca ampliada (sinônimo ou combinação parcial) — pode não ser exatamente o que você procurou."
                >
                  Resultado relacionado
                </span>
              )}
              {business.esta_contratando && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-100 text-teal-800">                  Contratando
                </span>
              )}
            </div>

            {(business.descricao_curta || business.descricao) && (
              <p className="text-sm text-[var(--text-secondary)] line-clamp-2 mb-2">
                {business.descricao_curta || business.descricao}
              </p>
            )}

            {business.rating_avg && business.rating_count > 0 && (
              <div className="flex items-center gap-2 mb-2">
                <StarRating rating={business.rating_avg} size="sm" />
                <span className="text-xs text-[var(--text-muted)]">
                  ({business.rating_count})
                </span>
              </div>
            )}

            {showDistance && business.distancia_km && (
              <p className="text-xs text-[var(--text-muted)] mb-2">
                {business.distancia_km.toFixed(1)} km de você
              </p>
            )}
          </div>
        </div>

        {/* Action buttons row */}
        {(business.whatsapp || business.instagram || business.telefone) && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--border)]">
            {business.whatsapp && (
              <button
                onClick={handleWhatsApp}
                className="flex-1 bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[#FF9500] rounded-full px-4 py-1.5 font-semibold text-xs sm:text-sm transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center justify-center gap-1.5"
                data-testid={`whatsapp-button-${business.id}`}
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </button>
            )}
            {business.instagram && (
              <button
                onClick={handleInstagram}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors text-[var(--text-secondary)]"
                data-testid={`instagram-button-${business.id}`}
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </button>
            )}
            {business.telefone && (
              <button
                onClick={handleCall}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors text-[var(--text-secondary)]"
                data-testid={`call-button-${business.id}`}
                aria-label="Ligar"
              >
                <Phone className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
      </article>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // Vertical variant (default) — used by Home carousels
  // ─────────────────────────────────────────────────────────────────
  return (
    <article
      onClick={handleCardClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
      className={`bg-white rounded-xl border border-[var(--border)] shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col snap-start group cursor-pointer relative ${
        gridFull
          ? 'w-full'
          : 'min-w-[196px] w-[196px] sm:w-[226px] sm:min-w-[226px]'
      }`}
      data-testid={`business-card-${business.id}`}
      role="link"
      tabIndex={0}
      aria-label={`${business.nome}${business.categoria_nome ? `, ${business.categoria_nome}` : ''}${typeof business.rating === 'number' ? `, avaliação ${business.rating} de 5` : ''}`}
    >
      {/* Favorite and Share buttons */}
      <div className="absolute top-2 right-2 z-10 flex gap-2">
        <ShareButton business={business} size="sm" />
        <FavoriteButton businessId={business.id} size="sm" />
      </div>

      {/* Logo / hero — square aspect ratio. When the business has no logo we
          render the same neutral placeholder used in the Explorar (horizontal)
          variant so cards stay visually consistent across the app. */}
      <div className="w-full aspect-square overflow-hidden bg-[#FFE9C7]">
        {logoSrc ? (
          <BackendImage
            src={logoSrc}
            alt={business.nome}
            className="w-full h-full object-cover object-center block group-hover:scale-105 transition-transform duration-300"
            fallback={(
              <div className="w-full h-full flex items-center justify-center text-[var(--primary)]">
                <CategoryIcon className="w-12 h-12 opacity-70" />
              </div>
            )}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--primary)]">
            <CategoryIcon className="w-12 h-12 opacity-70" />
          </div>
        )}
      </div>

      <div className="p-2.5 sm:p-3 flex-1">
        <div className="flex items-start justify-between mb-1.5">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm sm:text-lg mb-0.5 group-hover:text-[var(--primary)] transition-colors line-clamp-2 leading-tight" style={{ fontFamily: 'Manrope' }}>
              {business.nome}
            </h3>
            <p className="text-[11px] sm:text-sm text-[var(--text-secondary)] line-clamp-1">
              {business.subcategoria ? `${business.subcategoria} • ${business.bairro}` : `${business.categoria} • ${business.bairro}`}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mb-2">
          <BusinessLevelBadge businessId={business.id} />
          {/* Badge "Verificado" removido da exibição pública (iter 196). */}
          {isOpen && (
            <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-800">
              Aberto
            </span>
          )}
          {business.entrega && (
            <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-100 text-purple-800">
              Entrega
            </span>
          )}
          {showPromotion && (
            <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-orange-100 text-orange-800">
              Promoção
            </span>
          )}
          {campanha && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/campanhas/${campanha.campanha_id}`); }}
              data-testid={`campanha-badge-${business.id}`}
              className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium text-white hover:opacity-90"
              style={{ background: campanha.cor_destaque }}
              title={`Campanha: ${campanha.campanha_nome}`}
            >
              <Megaphone className="w-3 h-3 mr-1" />
              {campanha.campanha_nome.length > 16 ? campanha.campanha_nome.slice(0, 16) + '…' : campanha.campanha_nome}
            </button>
          )}
          {coletivo && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/coletivos/${coletivo.coletivo_slug}`); }}
              data-testid={`coletivo-badge-${business.id}`}
              className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 transition"
              title={`Membro do coletivo: ${coletivo.coletivo_nome}`}
            >
              <Handshake className="w-3 h-3 mr-1" />
              {coletivo.coletivo_nome.length > 16 ? coletivo.coletivo_nome.slice(0, 16) + '…' : coletivo.coletivo_nome}
            </button>
          )}
          {business.esta_contratando && (
            <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-teal-100 text-teal-800">
              Contratando
            </span>
          )}
        </div>

        <p className="text-xs sm:text-sm text-[var(--text-secondary)] line-clamp-3 mb-3 leading-snug">
          {business.descricao_curta || business.descricao}
        </p>

        {business.rating_avg && business.rating_count > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <StarRating rating={business.rating_avg} size="sm" />
            <span className="text-xs text-[var(--text-muted)]">
              ({business.rating_count})
            </span>
          </div>
        )}

        {showDistance && business.distancia_km && (
          <p className="text-xs text-[var(--text-muted)] mb-3">
            📍 {business.distancia_km.toFixed(1)} km de você
          </p>
        )}
      </div>

      <div className="px-4 pb-4 flex items-center gap-2">
        {business.whatsapp && (
          <button
            onClick={handleWhatsApp}
            className="flex-1 bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[#FF9500] rounded-full px-4 py-2 font-semibold text-sm transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center justify-center gap-1.5"
            data-testid={`whatsapp-button-${business.id}`}
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </button>
        )}
        {business.instagram && (
          <button
            onClick={handleInstagram}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors text-[var(--text-secondary)]"
            data-testid={`instagram-button-${business.id}`}
            aria-label="Instagram"
          >
            <Instagram className="w-5 h-5" />
          </button>
        )}
        {business.telefone && (
          <button
            onClick={handleCall}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors text-[var(--text-secondary)]"
            data-testid={`call-button-${business.id}`}
            aria-label="Ligar"
          >
            <Phone className="w-5 h-5" />
          </button>
        )}
      </div>
    </article>
  );
}

// Memoized: re-renders only if business.id changes (or other essential props)
export default memo(BusinessCard, (prev, next) => (
  prev.business?.id === next.business?.id &&
  prev.business?.updated_at === next.business?.updated_at &&
  prev.showDistance === next.showDistance &&
  prev.variant === next.variant
));
